import {useAuth} from "../auth/AuthHook";
import {CaseInfo, UserInfo} from "../support/model";
import {Empty, Table, TablePaginationConfig, Tag} from "antd";
import {Link, useNavigate, useLocation, useSearchParams} from "react-router-dom";
import {UserNameTag} from "../general/UserNameTag";
import React, {ReactElement, ReactPropTypes, useCallback, useEffect, useState} from "react";
import './CaseFilter.css'
import {ColumnsType, FilterValue, SorterResult, TableCurrentDataSource} from "antd/es/table/interface";
import {useSupportCase} from "../support/CaseHook";
import moment from "moment";
import {LockIcon} from "../components/Icons";
import {SelectableLink} from "../components/SelectableLink";

interface MainFilterProps {
    cases: CaseInfo[];
}


interface FilterDefinition {
    key: string;
    title: string;
    render: (value: any | null, record: CaseInfo) => ReactElement;
    asStringValue?: (record: CaseInfo) => string;
    filterType?: "text" | null;
}

interface TableConfig {
    tableTitle: string;
    filters?: Record<string, string[] | null>;
    sort?: Record<string, "ascend" | "descend">;
}

const configMap: Record<string, (userId: string) => TableConfig> = {
    "main": () => {
        return {
            tableTitle: "All Cases",
            filters: {
                "severity": null,
                "status": null,
                "learning_extract.state": null,
                "created_by": null,
            },
            sort: {
                "creation_time": "descend",
            }
        }
    },
    "open": () => {
        return {
            tableTitle: "Open Cases",
            filters: {
                "status": ["open"],
                "learning_extract.state": null,
                "created_by": null,
            },
            sort: {
                "creation_time": "descend",
            }
        }
    },
    "mycases": (userName) => {
        return {
            tableTitle: "My Cases",
            filters: {
                "created_by": [userName],
                "learning_extract.state": null,
                "status": null,
            },
            sort: {
                "creation_time": "descend",
            }
        }
    },
    "learnt": () => {
        return {
            tableTitle: "Learnt Cases",
            filters: {
                "learning_extract.state": ["published"],
                "created_by": null,
                "status": null,
            },
            sort: {
                "creation_time": "descend",
            }
        }
    },
    "learn_proposed": () => {
        return {
            tableTitle: "Proposed Learning Cases",
            filters: {
                "learning_extract.state": ["proposed"],
                "created_by": null,
                "status": null,
            },
            sort: {
                "creation_time": "descend",
            }
        }

    }

}

const getConfigByName = (name: string, userData?: UserInfo): TableConfig => {
    if (configMap[name] !== undefined) {
        return configMap[name](userData?.name ?? "");
    }
    return {
        tableTitle: "No filter",
        filters: {},
        sort: {}
    };
}

const getFilterConfigFromSearchParams = (searchParams: URLSearchParams): TableConfig => {
    const filterConfig = JSON.parse(searchParams.get("filters") ?? "{}") as Record<string, string[] | null>;
    const sortSearchParam = searchParams.get("sort") ?? "{}";
    const sortConfig = Object.fromEntries(
        sortSearchParam
            .split(",")
            .map((s) => s.split(";"))
            .filter(([field, order]) => ((field != undefined) && order)
            )
    )
    return {
        tableTitle: "Filtered Cases",
        filters: filterConfig,
        sort: sortConfig
    }

}

export function CaseFilter() {
    const {isLoggedIn, userData, setOrganization} = useAuth()
    const {fetchData} = useSupportCase()
    const [searchParams, setSearchParams] = useSearchParams();
    const [cases, setCases] = useState<CaseInfo[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const location = useLocation();
    const filterId = searchParams.get("fid");
    const config = filterId != null ? getConfigByName(filterId, userData) : getFilterConfigFromSearchParams(searchParams);
    const [idNameMap, setIdNameMap] = useState<Record<string, string>>({});
    const [searchTerm, setSearchTerm] = useState<string>("");
    const navigate = useNavigate();

    const doFetchHistory = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetchData(`api/cases/`, "GET")
            if (response.ok || response.status <= 299) {
                setCases(await response.json())
            }
        } catch (e) {
            // ignore
        }
        setLoading(false);
    }, [fetchData])

    const fetchHistory = useCallback(async () => {
        if (isLoggedIn()) {
            await doFetchHistory();
        } else {
            setCases((prevCases) => {
                if (prevCases.length > 0) {
                    return []
                }
                return prevCases;
            })
        }
    }, [isLoggedIn, doFetchHistory])

    useEffect(() => {
        fetchHistory()
    }, [fetchHistory, location.pathname])

    const columnDefs: FilterDefinition[] = [
        {
            title: "Title",
            key: "title",
            render: (title: string | null, record: CaseInfo) => {
                return (
                    <Link to={`/cases/${record.case_id}?org_id=${record.org_id}`}  className={"text-blue-400"}>
                        {title}
                    </Link>
                )
            },
            filterType: null,
        },
        {
            title: "Organization",
            key: "org_id",
            render: (org: string | null) => {
                return (
                    <span className={""}>
                        {org ?? "-"}
                    </span>
                )
            },
        },
        {
            title: "Created By",
            key: "created_by",
            render: (userId: string | null) => {
                return isLoggedIn() && userId != null ?
                    <UserNameTag userId={userId} extraClassNames={"font-bold text-sm"}/> :
                    <span className={"text-gray-really font-bold text-sm"}>User with id: {userId ?? "null"}</span>;
            },
            asStringValue: (record: CaseInfo) => {
                return idNameMap[record.created_by ?? ""] ?? record.created_by ?? "";
            },
        },
        {
            title: "Status",
            key: "status",
            render: (status: string | null) => {
                if (status === null || status === undefined || status === "") {
                    return <div className="w-4 h-4">{"-"}</div>;
                }
                const classNameMap: Record<string, string> = {
                    "open": "bg-blue-500 text-white",
                    "closed": "bg-red-500 text-white",
                    "escalated": "bg-yellow-500 text-black",
                }
                return (
                    <Tag className={`${classNameMap[status.toLowerCase()] ?? "bg-gray-500 text-white"} border-none`}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}

                    </Tag>
                )
            }
        },
        {
            title: "Creation Time",
            key: "creation_time",
            render: (time: number | null, _: CaseInfo) => {
                if (time === null || time === undefined) {
                    return <span>-</span>;
                }
                return <span className={""}>{moment(new Date(time * 1000)).format("YYYY-MM-DD HH:mm")}</span>;
            },
            asStringValue: (record: CaseInfo) => {
                return record.creation_time != null ? moment(new Date(record.creation_time * 1000)).format("YYYY-MM-DD HH:mm") : "";
            },
        },
        {
            title: "Learning status",
            key: "learning_extract.state",
            asStringValue: (record: CaseInfo) => {
                return record.learning_extract?.state ?? "none";
            },
            render: (state: string | null, _: CaseInfo) => {
                return (
                    <span className={``}>
                        {state ?? "none"}
                    </span>
                )

            }
        }
    ];

    const basicValueMapper = (key: string, caseInfo: CaseInfo): string => {
        const val = caseInfo[key as keyof CaseInfo];
        if (val === undefined || val === null) {
            return "";
        }
        return val;
    }

    const sortedColumns: ColumnsType<CaseInfo> = columnDefs
        .map((col) => {
            const asStringValue = col.asStringValue ?? ((record: CaseInfo) => {
                return basicValueMapper(col.key, record);
            })
            const uniqValues = cases
                .map((caseInfo) => {
                    return asStringValue(caseInfo);
                })
                .reduce((acc, value) => {
                    acc.add(value);
                    return acc;
                }, new Set<string>())
            return {
                key: col.key,
                title: col.title,
                dataIndex: col.key.includes(".") ? col.key.toString().split(".") : col.key,
                render: col.render,
                sorter: (a: CaseInfo, b: CaseInfo) => {
                    const aValue = asStringValue(a);
                    const bValue = asStringValue(b);
                    return aValue.localeCompare(bValue);
                },
                sortOrder: config.sort != undefined ? config.sort[col.key] : undefined,
                filters: col.filterType === null ? undefined : Array.from(uniqValues).map((value) => {
                    return {
                        text: value,
                        value: value
                    }
                }),
                filteredValue: col.filterType === null || config.filters === undefined ? [] : config.filters[col.key],
                onFilter: col.filterType === null ? undefined : (value: boolean | React.Key, record: CaseInfo) => {
                    const recordValue = asStringValue(record);
                    return recordValue.toLowerCase() === value.toString().toLowerCase();
                },
            };
        })


    const changeHandler = useCallback((pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<CaseInfo> | SorterResult<CaseInfo>[], extra: TableCurrentDataSource<CaseInfo>) => {
        const sorters = Array.isArray(sorter) ? sorter : [sorter];
        console.log("Sorters:", sorters);

        const sortString = sorters.filter((s) => s.field && s.order).map((s) => `${s.field?.toString().replaceAll(",", ".")};${s.order}`).join(",");
        console.log("Filters:", filters);
        setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.delete("fid");
            newParams.set("page", pagination.current?.toString() ?? "1");
            newParams.set("pageSize", pagination.pageSize?.toString() ?? "10");
            newParams.set("sort", sortString);
            newParams.set("filters", JSON.stringify(
                Object.fromEntries(Object.entries(filters).filter(([key, value]) => value !== null && value.length > 0))
            ));
            return newParams;
        });
    }, [setSearchParams]);
    const pagination: TablePaginationConfig = {
        position: ["bottomCenter"],
        size: "small",
        total: cases.length,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50", "100"],
        defaultPageSize: 10,
        current: parseInt(searchParams.get("page") ?? "1"),
        pageSize: parseInt(searchParams.get("pageSize") ?? "10"),
    }
    const creators = cases.map((caseInfo) => {
        return caseInfo.created_by ?? "";
    })
    const uniqueCreators = Array.from(new Set(creators));
    const linkIdAndNamePairs = [
        {fid: "main", name: "No filters"},
        {fid: "open", name: "Open Cases"},
        {fid: "mycases", name: "My Cases"},
        {fid: "learnt", name: "Learnt Cases"},
        {fid: "learn_proposed", name: "Proposed for learning"},
    ]
    const searchTermWords = searchTerm.trim().toLowerCase().split(" ").filter((word) => word.length > 0);
    const filteredCases = cases.filter((caseInfo) => {
        const stringValue = caseInfo.title.toLowerCase();
        return searchTermWords.every((word) => stringValue.includes(word));
    })

    return (
        <div className="w-full lg:px-16 xl:px-32">
            <div hidden={true} id="hidden-username-fetchers">
                {
                    uniqueCreators.map((creator) => {
                        return <UserNameTag key={`hidden-username-fetcher-${creator}`} userId={creator} userNameCallback={(name) => {
                            if (idNameMap[creator] == name) {
                                return;
                            }
                            setIdNameMap((prevMap) => {
                                return {
                                    ...prevMap,
                                    [creator]: name
                                }
                            })
                        }}/>
                    })
                }
            </div>
            {
                !isLoggedIn() &&
                    <Empty description={
                        <span className="text-gray-300 text-base">
                            You need to log in to see cases in your organization.
                        </span>
                    } image={<LockIcon/>}/>
            }
            { isLoggedIn() &&
                <div className={"flex flex-col gap-8 pt-4"}>
                    <div className={"flex flex-row gap-4 items-center justify-self-center justify-center w-full"}>
                        <input
                            type={"text"}
                            name={"search"}
                            id={"search"}
                            placeholder={"Search ..."}
                            className={"justify-self-center rounded-xl bg-gray-really text-white w-1/2 focus:ring-0 focus:border focus:border-gray-400"}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className={"flex flex-row gap-2 items-center justify-start px-2"}>
                        <div className={"text-nowrap"}>
                            Reset to:
                        </div>
                        {linkIdAndNamePairs.map((pair) => (
                            <SelectableLink key={`filter-link-${pair.fid}`} className={"rounded-xl bg-gray-really text-white border border-gray-400 py-1 h-fit w-fit block px-2 hover:border-neutral-500 text-sm text-nowrap"} hasBorder={true} to={`/filter?fid=${pair.fid}`} isSelected={filterId === pair.fid}>
                                {pair.name}
                            </SelectableLink>
                        ))}
                    </div>
                    <Table<CaseInfo>
                        loading={loading}
                        prefixCls={"arten-search"}
                        columns={sortedColumns}
                        dataSource={filteredCases}
                        rowKey="case_id"
                        pagination={pagination}
                        rowClassName={"case-filter-table-row"}
                        className={"case-filter-table"}
                        rootClassName={"case-filter-table-root"}
                        bordered={false}
                        sortDirections={["ascend", "descend"]}
                        showSorterTooltip={true}
                        components={
                            {
                                header: {
                                    wrapper: (props: ReactPropTypes) => (
                                        <thead {...props} className={"case-filter-table-header"}/>
                                    ),
                                },

                            }
                        }
                        onChange={changeHandler}
                    />
                </div>

            }
        </div>
    );

}
